// src/queue.ts
var isNil = (x) => x === void 0 || x === null;
var Queue = class {
  constructor() {
    this._queue = [];
  }
  get length() {
    return this._queue.length;
  }
  enqueue(item) {
    if (!isNil(item)) {
      this._queue.push(item);
    }
  }
  dequeue() {
    return this._queue.shift();
  }
  flush() {
    const items = this._queue;
    this._queue = [];
    return items;
  }
};

// src/wait-for-document-loaded.ts
var waitForDocumentLoaded = async () => new Promise((resolve, reject) => {
  if (!window && !document)
    reject(new Error("window/document do not exist"));
  if (document.readyState === "complete") {
    resolve();
  } else {
    window.addEventListener("load", () => {
      resolve();
    });
  }
});
var wait_for_document_loaded_default = waitForDocumentLoaded;

// src/tracker.ts
var isFunction = (f) => !isNil(f) && typeof f === "function";
var isPromiseLike = (x) => isFunction(x?.then);
var Tracker = class {
  constructor(config) {
    this.config = config;
    this.enabled = config?.enabled ?? true;
    this.deferUntilLoaded = config?.deferUntilLoaded ?? true;
    this.events = config.events;
    this.initTimeout = config.initTimeout ?? 1e3 * 20;
    this.name = config.name;
    this.queue = new Queue();
    this.isReady = false;
  }
  // A tracker is considered ready once the `initialize` function has resolved
  async initialize() {
    if (!this.enabled) {
      return this.isReady;
    }
    if (!this.config.initialize) {
      this.isReady = true;
      return this.isReady;
    }
    try {
      if (this.deferUntilLoaded)
        await wait_for_document_loaded_default();
      const initializeResult = this.config.initialize();
      if (isPromiseLike(initializeResult)) {
        await Promise.race([
          initializeResult,
          new Promise((resolve, reject) => {
            setTimeout(
              () => reject(
                new Error(
                  `tracker initialization timed out after ${this.initTimeout} milliseconds`
                )
              ),
              this.initTimeout
            );
          })
        ]);
      }
    } catch (error) {
      error.message = `Error initializing tracker "${this.name}": ${error.message}`;
      throw error;
    }
    try {
      this.isReady = true;
      this.flushAndTrack();
    } catch (error) {
      error.message = `Error sending queued data to ${this.name}: ${error.message}`;
      throw error;
    }
    return this.isReady;
  }
  // Once the tracker has initialized, any queued events should be flushed and sent.
  flushAndTrack() {
    const queuedEvents = this.queue.flush();
    for (const { eventName, eventPayload } of queuedEvents) {
      this.track(eventName, eventPayload);
    }
  }
  /**
   * This function determines if `events` is a function or an event map and calls the
   * appropriate function depending on how `events` is defined.
   */
  track(eventName, eventPayload) {
    if (!this.enabled || isNil(this.events)) {
      return;
    }
    if (!this.isReady) {
      this.queue.enqueue({ eventName, eventPayload });
      return;
    }
    try {
      if (isFunction(this.events)) {
        this.events(eventName, eventPayload);
      } else if (isFunction(this.events[eventName])) {
        this.events[eventName]?.(eventPayload);
      }
    } catch (error) {
      error.message = `Error sending tracking data to ${this.name}: ${error.message}`;
      throw error;
    }
  }
};
var tracker_default = Tracker;

// src/define-trackers.ts
var isClient = typeof window !== "undefined";
var defineTrackers = (trackerConfigs = [], options = {}) => {
  const logger = options?.logger ?? console;
  const trackerMap = {};
  if (isClient) {
    for (const trackerConfig of trackerConfigs) {
      trackerMap[trackerConfig.name] = new tracker_default(trackerConfig);
    }
  }
  const trackers = Object.values(trackerMap);
  const areInitialized = Promise.allSettled(trackers.map((tracker) => tracker.initialize())).then((results) => {
    return results.reduce(
      (accumulator, result) => accumulator.concat([result.status === "fulfilled" && result.value === true]),
      []
    );
  }).catch((error) => {
    console.error({ error });
  });
  return {
    // This function will propagate tracking events to the defined trackers.
    track: (eventName, eventPayload = {}) => {
      if (isClient) {
        for (const tracker of trackers) {
          try {
            tracker.track(eventName, eventPayload);
          } catch (error) {
            logger?.error?.(error.message);
          }
        }
      }
    },
    areInitialized,
    trackers
  };
};
var define_trackers_default = defineTrackers;

// src/execute-script.ts
var executeScript = (scriptContent) => {
  const scriptElement = document.createElement("script");
  scriptElement.type = "text/javascript";
  scriptElement.text = scriptContent;
  document.body.append(scriptElement);
};
var execute_script_default = executeScript;

// src/wait-for-global.ts
var waitForGlobal = async (key) => {
  let requestId;
  const callback = (resolve) => {
    if (!window[key]) {
      requestId = window.requestAnimationFrame(callback.bind(void 0, resolve));
      return;
    }
    if (!isNil(requestId))
      window.cancelAnimationFrame(requestId);
    resolve();
  };
  return new Promise(callback);
};
var wait_for_global_default = waitForGlobal;
export {
  Queue,
  define_trackers_default as defineTrackers,
  execute_script_default as executeScript,
  wait_for_document_loaded_default as waitForDocumentLoaded,
  wait_for_global_default as waitForGlobal
};
//# sourceMappingURL=index.mjs.map